/**
 * ProtectedRoute — guard that redirects unauthenticated users to /login.
 *
 * Behavior:
 *   - While auth bootstrap is loading → render nothing (avoids a redirect flash)
 *   - If the user is null after bootstrap → redirect to /login, preserving
 *     the original URL in `state.from` so we can return them there post-login
 *   - If the user is logged in but has no active Meraki key → redirect to
 *     /meraki/keys (the SetMerakiKeyPage), so they don't sit on fixture data
 *     wondering why nothing is real. Bypassed when already on /meraki/keys
 *     (otherwise we'd loop) or on /design-system (the showcase page is
 *     intentionally key-less).
 *   - Otherwise → render the children
 *
 * The guard is opt-in via the env var `VITE_REQUIRE_AUTH`. While the Python
 * API isn't wired, ship the bundle with `VITE_REQUIRE_AUTH=false` so anyone
 * can click through the app — flip to "true" once /api/auth/me responds for real.
 */
import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/stores/auth";

const REQUIRE_AUTH = import.meta.env.VITE_REQUIRE_AUTH === "true";

// Pages that don't need a Meraki key — the user must be able to reach
// /meraki/keys to register one (otherwise the gate would loop), and the
// design system + login + logout should always be reachable too.
const KEYLESS_PATHS = new Set([
  "/meraki/keys",
  "/design-system",
  "/login",
  "/logout",
]);

export interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const status = useAuth((s) => s.status);
  const user = useAuth((s) => s.user);
  const location = useLocation();

  // Auth not enforced (default during the migration) — pass through.
  if (!REQUIRE_AUTH) return <>{children}</>;

  // Bootstrap in flight; render nothing rather than briefly redirecting.
  if (status === "idle" || status === "loading") return null;

  // Bootstrap done, no user — bounce to login and remember where they came from.
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // No active Meraki key → guide them to register one. Avoids the silent
  // "all pages show fixture data" UX. The flag is set by /api/auth/me;
  // if undefined (older shim), assume true so we don't lock anyone out.
  const needsKey = user.merakiKeyConfigured === false;
  if (needsKey && !KEYLESS_PATHS.has(location.pathname)) {
    return <Navigate to="/meraki/keys" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
